import { useRef, useState } from "react";
import { faCube } from "@fortawesome/free-solid-svg-icons";
import { BLUE, CORAL, MUTED, NAVY } from "../constants";
import { exportTablePng } from "../exportUtils";
import { formatCurrency, formatInteger } from "../formatters";
import { SectionTitle } from "./SectionTitle";
import { TrendValue } from "./TrendValue";

export function SurchargeTable({ rows, isLoading }) {
  const sectionRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const handleExport = async () => {
    if (isLoading) return;
    setIsExporting(true);
    try { await exportTablePng(sectionRef, "Top_10_Surcharges"); }
    finally { setIsExporting(false); }
  };
  const maxSpend = rows.length ? Math.max(...rows.map((r) => Number(r.spend || 0))) : 1;
  return (
    <section ref={sectionRef}>
      <SectionTitle title="Top 10 Surcharges by Spend" icon={faCube} rulesKey="top10Surcharges" onExport={handleExport} isExporting={isExporting} disabled={isLoading} />
      <div className="overflow-hidden rounded-2xl border border-[#d6dfeb] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-[#e4e9f2] text-sm">
            <thead style={{ background: NAVY }}>
              <tr>
                <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-black uppercase tracking-[0.08em] text-white w-8">#</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-black uppercase tracking-[0.08em] text-white">Surcharge</th>
                <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-black uppercase tracking-[0.08em] text-white">Spend $</th>
                <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-black uppercase tracking-[0.08em] text-[#8da6cc] w-36">vs Max</th>
                <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-black uppercase tracking-[0.08em] text-[#8da6cc]">Prior Q Spend $</th>
                <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-black uppercase tracking-[0.08em] text-white">Spend $ QoQ</th>
                <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-black uppercase tracking-[0.08em] text-white">Consignments</th>
                <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-black uppercase tracking-[0.08em] text-[#8da6cc]">Prior Q Cons</th>
                <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-black uppercase tracking-[0.08em] text-white">Cons QoQ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e4e9f2]" style={{ color: NAVY }}>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center font-semibold" style={{ color: MUTED }}>
                    Loading data...
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((row, index) => {
                  const spend    = Number(row.spend || 0);
                  const barPct   = maxSpend > 0 ? (spend / maxSpend) * 100 : 0;
                  const barColor = index === 0 ? CORAL : index < 3 ? BLUE : "#94a3b8";
                  return (
                    <tr key={`${row.surcharge}-${index}`} className="odd:bg-white even:bg-[#f6f8fc]">
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex w-6 h-6 items-center justify-center rounded-md text-[11px] font-black text-white"
                          style={{ background: barColor }}
                        >
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-black whitespace-nowrap">{row.surcharge || "—"}</td>
                      <td className="px-4 py-3 text-right font-black whitespace-nowrap">{formatCurrency(spend)}</td>
                      <td className="px-4 py-3">
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: "#e4e9f2" }}>
                          <div className="h-full rounded-full" style={{ width: `${barPct}%`, background: barColor }} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap" style={{ color: MUTED }}>{formatCurrency(row.priorQSpend)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap"><TrendValue value={row.spendQoqPct} /></td>
                      <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{formatInteger(row.consignments)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap" style={{ color: MUTED }}>{formatInteger(row.priorQConsignments)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap"><TrendValue value={row.consignmentsQoqPct} /></td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center font-semibold" style={{ color: MUTED }}>
                    No surcharge data for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
